import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { patientAPI } from '../../lib/api';
import { formatDate, calculateAge, getStatusColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Search, UserPlus, Eye, Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';

const PatientList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Get patient_type from URL query parameter
  const queryParams = new URLSearchParams(location.search);
  const typeFromUrl = queryParams.get('patient_type');
  const [patientType, setPatientType] = useState(typeFromUrl || 'all');

  useEffect(() => {
    // Update filter if URL parameter changes
    if (typeFromUrl && typeFromUrl !== patientType) {
      setPatientType(typeFromUrl);
    }
  }, [typeFromUrl, patientType]);

  const fetchPatients = useCallback(async () => {
    try {
      const params = {};
      if (patientType !== 'all') params.patient_type = patientType;
      
      const response = await patientAPI.getAll(params);
      // Handle paginated response structure
      const patientsData = response.data.patients || response.data;
      setPatients(Array.isArray(patientsData) ? patientsData : []);
    } catch (error) {
      toast.error('Failed to load patients');
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [patientType]);


  useEffect(() => {
    fetchPatients();
  }, [patientType, fetchPatients]);
  const filteredPatients = patients.filter(patient => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      patient.full_name?.toLowerCase().includes(searchLower) ||
      patient.patient_id?.toLowerCase().includes(searchLower) ||
      patient.phone?.includes(search)
    );
  });

  return (
    <div data-testid="patients-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Patients</h1>
            <p className="text-slate-500">Manage patient records</p>
          </div>
          <Button
            onClick={() => navigate('/patients/register')}
            className="bg-red-500 hover:bg-red-600 rounded-full"
            data-testid="register-patient-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Register Patient
          </Button>
        </div>
      </header>

      <div className="page-content">
        <Card>
          <CardHeader className="border-b border-slate-100">
            <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
              <CardTitle className="text-lg font-semibold font-[Manrope]">
                All Patients ({filteredPatients.length})
              </CardTitle>
              <div className="flex gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, ID, or phone..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 w-64"
                    data-testid="search-input"
                  />
                </div>
                <Select value={patientType} onValueChange={setPatientType}>
                  <SelectTrigger className="w-40" data-testid="patient-type-filter">
                    <SelectValue placeholder="Patient Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="indoor">Indoor</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading patients...</div>
            ) : filteredPatients.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No patients found. Register a new patient to get started.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Age/Gender</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Registered</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPatients.map((patient) => (
                    <TableRow key={patient.id} data-testid={`patient-row-${patient.id}`}>
                      <TableCell className="font-mono text-sm">{patient.patient_id}</TableCell>
                      <TableCell className="font-medium">{patient.full_name}</TableCell>
                      <TableCell>
                        {calculateAge(patient.date_of_birth)} yrs / {patient.gender?.charAt(0).toUpperCase()}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1 text-sm">
                            <Phone className="w-3 h-3" /> {patient.phone}
                          </span>
                          {patient.email && (
                            <span className="flex items-center gap-1 text-sm text-slate-500">
                              <Mail className="w-3 h-3" /> {patient.email}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={patient.patient_type === 'indoor' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}>
                          {patient.patient_type === 'indoor' ? 'Indoor' : 'OPD'}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(patient.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/patients/${patient.id}`)}
                          data-testid={`view-patient-${patient.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PatientList;
